/**
 * License Activation Page
 *
 * Admin interface for dual-gate activation workflow:
 * - Import Xero invoice data (JSON or manual form)
 * - View and manage entitlements
 * - Pass payment and license gates
 * - Activate entitlements
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ActivationStatus,
  EntitlementsList,
  ErrorAlert,
  ImportSection,
  LoadingState,
  PageHeader,
  StatsSummary,
} from './license-activation/license-activation.parts'
import { useLicenseActivation } from './license-activation/use-license-activation'

export function LicenseActivationPage(): ReactElement {
  const { t } = useTranslation('admin')
  const state = useLicenseActivation()

  if (state.loading) return <LoadingState />

  return (
    <div className="space-y-6">
      <PageHeader
        onRefresh={() => {
          void state.loadData()
        }}
        loading={state.loading}
        t={t}
      />
      {state.error && (
        <ErrorAlert
          error={state.error}
          onDismiss={() => {
            state.setError(null)
          }}
          t={t}
        />
      )}
      <ImportSection
        showImportSection={state.showImportSection}
        setShowImportSection={state.setShowImportSection}
        importMode={state.importMode}
        setImportMode={state.setImportMode}
        onImportSuccess={(result) => {
          void state.handleImportSuccess(result)
        }}
        onImportError={state.handleImportError}
        t={t}
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <EntitlementsList
          entitlements={state.entitlements}
          xeroStates={state.xeroStates}
          selectedEntitlementId={state.selectedEntitlementId}
          setSelectedEntitlementId={state.setSelectedEntitlementId}
          t={t}
        />
        <ActivationStatus
          selectedEntitlement={state.selectedEntitlement}
          selectedXeroState={state.selectedXeroState}
          isProcessing={state.isProcessing}
          onPassPaymentGate={() => {
            void state.handlePassPaymentGate()
          }}
          onPassLicenseGate={() => {
            void state.handlePassLicenseGate()
          }}
          onActivate={() => {
            void state.handleActivate()
          }}
          t={t}
        />
      </div>
      <StatsSummary entitlements={state.entitlements} t={t} />
    </div>
  )
}
